import React, { useState } from 'react'
import Modal from '../../Shared/Modal'
import './PromptTutorialModal.scss'

interface PromptTutorialModalProps {
  onClose: () => void
}

const sections = [
  {
    title: 'Screens',
    prompts: [
      'Add a new screen called User Details',
      'Delete the Settings screen',
      'Rename the Home screen to Dashboard',
      'Duplicate the Profile screen',
      'List all my screens',
      'Describe what is on the Home screen',
      'Clear all components from the Settings screen',
    ],
  },
  {
    title: 'Screen Protection',
    prompts: [
      'Protect the Admin Panel so only admins can access it',
      'Restrict the Settings screen to admin users and redirect others to Home',
      'Remove admin protection from the Dashboard screen',
    ],
  },
  {
    title: 'Components',
    prompts: [
      'Add a button on the Home screen that goes to Profile',
      'Add a text component on the Welcome screen that says "Hello World"',
      'Add an image to the top of the Home screen',
      'Add a search bar to the Users screen',
      'Remove the subtitle from the Home screen',
    ],
  },
  {
    title: 'Lists',
    prompts: [
      'Add a list of Tasks to the Home screen',
      'Add a card list of Products to the Shop screen',
      'Change the title of the list on the Home screen to "My Items"',
    ],
  },
  {
    title: 'Forms & Inputs',
    prompts: [
      'Add a date picker to the Create Event screen',
      'Add a dropdown on the Settings screen connected to Categories',
      'Add a toggle to the Profile screen',
      'Add a file picker to the Upload screen',
      'Change the submit button text on the Create Tasks form to "Save Task"',
      'Rename the "email" field on the Sign Up form to "Email Address"',
    ],
  },
  {
    title: 'Text Styling',
    prompts: [
      'Change the title on the Home screen to "Welcome Back"',
      'Make the title on the Dashboard bold',
      'Change the font size of the heading on Home to 28',
      'Change the subtitle color on the Welcome screen to blue',
      'Make the description on the About screen italic',
    ],
  },
  {
    title: 'Colors & Branding',
    prompts: [
      'Change the primary color to purple',
      'Change the Sign Up button color on the Welcome screen to green',
      'Change the app bar color on the Home screen to dark blue',
      'Change the background of the Login screen to light gray',
      'Suggest some colors for my app',
    ],
  },
  {
    title: 'Navigation',
    prompts: [
      'Add bottom navigation with tabs Home, Search, and Profile',
      'Add a side navigation menu to the Dashboard',
      'Add an app bar to the Settings screen',
      'Set the active tab to Profile in the bottom navigation',
    ],
  },
  {
    title: 'Layout',
    prompts: [
      'Move the image above the title on the Home screen',
      'Swap the button and the text on the Welcome screen',
      'Make the image on the Profile screen bigger',
      'Add a separator between the title and the list on Home',
    ],
  },
  {
    title: 'Data & Collections',
    prompts: [
      'Add a Status field to the Tasks collection',
      'Describe the Users collection',
      'Show me all the data in Products',
      'Generate dummy data for the Tasks collection',
    ],
  },
  {
    title: 'App Settings',
    prompts: ['Change the app name to MyApp', 'Regenerate onboarding screen'],
  },
]

const PromptTutorialModal: React.FC<PromptTutorialModalProps> = ({
  onClose,
}) => {
  const [copiedPrompt, setCopiedPrompt] = useState<string | null>(null)

  const handleCopy = (prompt: string) => {
    navigator.clipboard.writeText(prompt).catch(() => {})
    setCopiedPrompt(prompt)
    setTimeout(() => setCopiedPrompt(null), 1500)
  }

  return (
    <Modal onClose={onClose} size="md" grayOut scrolling>
      <div>
        <Modal.Header>
          <h2 className="prompt-tutorial-title">Ada Prompt Examples</h2>
        </Modal.Header>
        <Modal.Content>
          <p className="prompt-tutorial-intro">
            We are constantly making Ada better however she can already
            accomplish a lot! Here are examples of what you can ask Ada to do.
            Click any prompt to copy it to your clipboard. Click outside of the
            popup to return to the Builder.
          </p>
          <div className="prompt-tutorial-sections">
            {sections.map(section => (
              <div key={section.title} className="prompt-tutorial-section">
                <h3 className="prompt-tutorial-section-title">
                  {section.title}
                </h3>
                <ul className="prompt-tutorial-list">
                  {section.prompts.map(prompt => (
                    <li key={prompt}>
                      <button
                        type="button"
                        className={`prompt-tutorial-item${
                          copiedPrompt === prompt ? ' copied' : ''
                        }`}
                        onClick={() => handleCopy(prompt)}
                      >
                        {copiedPrompt === prompt ? 'Copied!' : prompt}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Modal.Content>
      </div>
    </Modal>
  )
}

export default PromptTutorialModal
